import { Box, SxProps } from '@mui/material';
import { Image } from 'src/components/image';
import { CONFIG } from 'src/config-global';
import { Theme } from 'src/theme/types';
import { SparkMediaDto } from '../../../../../libs/contracts/src/lib/common/media/media.dto';
import { FileTypeEnum } from '../../../../../libs/file-uploader/src/lib/enums/file-type.enum';

type Props = {
  media: SparkMediaDto[];
  onOpenLightbox: (src: string) => void;
  variant?: 'grid' | 'list-grid' | 'single';
};

// ============================================================================
// Remaining Images Overlay Component
// ============================================================================

type RemainingImagesOverlayProps = {
  count: number;
};

const RemainingImagesOverlay = ({ count }: RemainingImagesOverlayProps) => (
  <Box
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      color: 'white',
      fontSize: { xs: 20, md: 28 },
      fontWeight: 700,
      borderRadius: 2,
      cursor: 'pointer',
      transition: (theme) => theme.transitions.create('backgroundColor'),
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
      },
    }}
  >
    +{count}
  </Box>
);

// ============================================================================
// Gallery Item Component
// ============================================================================

type GalleryItemProps = {
  media: SparkMediaDto;
  sx?: SxProps<Theme>;
  alt?: string;
  src: string;
};

const GalleryItem = ({ media, alt, src, ...props }: GalleryItemProps) => {
  const isVideo = media.type === FileTypeEnum.VIDEO;

  if (isVideo) {
    return (
      <Box
        component="video"
        src={src}
        controls={false}
        autoPlay
        loop
        muted
        {...props}
      />
    );
  }

  return (
    <Image
      src={src}
      alt={alt}
      loading="lazy"
      slotProps={{ sx: props.sx }}
      {...props}
    />
  );
};

// ============================================================================
// No Images View Component
// ============================================================================

const NoImagesView = () => (
  <Box
    sx={{
      width: 1,
      height: { xs: 300, md: 500 },
      borderRadius: 2,
      overflow: 'hidden',
      cursor: 'pointer',
    }}
  >
    <GalleryItem
      media={{
        id: 1,
        type: FileTypeEnum.IMAGE,
        link: CONFIG.noImage,
      }}
      alt="No images"
      src={CONFIG.noImage}
      sx={{
        width: 1,
        height: { xs: 300, md: 500 },
        objectFit: 'contain',
        transition: (theme) => theme.transitions.create('transform'),
        '&:hover': {
          transform: 'scale(1.05)',
        },
      }}
    />
  </Box>
);

// ============================================================================
// Single Image View Component
// ============================================================================

type SingleImageViewProps = {
  media: SparkMediaDto;
  onOpenLightbox: (src: string) => void;
};

const SingleImageView = ({ media, onOpenLightbox }: SingleImageViewProps) => (
  <Box
    sx={{
      width: 1,
      height: { xs: 300, md: 500 },
      borderRadius: 2,
      overflow: 'hidden',
      cursor: 'pointer',
    }}
    onClick={() => onOpenLightbox(media.link)}
  >
    <GalleryItem
      media={media}
      alt="Gallery"
      src={media.link}
      sx={{
        width: 1,
        height: { xs: 300, md: 500 },
        objectFit: 'contain',
        transition: (theme) => theme.transitions.create('transform'),
        '&:hover': {
          transform: 'scale(1.05)',
        },
      }}
    />
  </Box>
);

// ============================================================================
// Grid View Component
// ============================================================================

type GridViewProps = {
  media: SparkMediaDto[];
  onOpenLightbox: (src: string) => void;
};

const GridView = ({ media, onOpenLightbox }: GridViewProps) => (
  <Box
    sx={{
      display: 'grid',
      gap: 1,
      gridTemplateColumns: 'repeat(2, 1fr)',
      borderRadius: 2,
      overflow: 'hidden',
    }}
  >
    {media.slice(0, 4).map((item, index) => {
      const isLastImage = index === 3 && media.length > 4;
      const remainingImages = media.length - 4;

      return (
        <Box
          key={item.link}
          sx={{
            position: 'relative',
            cursor: 'pointer',
            overflow: 'hidden',
            aspectRatio: '1/1',
          }}
          onClick={() => onOpenLightbox(item.link)}
        >
          <GalleryItem
            media={item}
            alt={`Gallery ${index + 2}`}
            src={item.link}
            sx={{
              width: 1,
              height: 1,
              objectFit: 'cover',
              transition: (theme) =>
                theme.transitions.create(['transform', 'opacity']),
              '&:hover': {
                transform: 'scale(1.05)',
                opacity: 0.9,
              },
            }}
          />
          {isLastImage && remainingImages > 0 && (
            <RemainingImagesOverlay count={remainingImages} />
          )}
        </Box>
      );
    })}
  </Box>
);

// ============================================================================
// List Grid View Components
// ============================================================================

type ListGridMainImageProps = {
  media: SparkMediaDto;
  onOpenLightbox: (src: string) => void;
};

const ListGridMainImage = ({
  media,
  onOpenLightbox,
}: ListGridMainImageProps) => (
  <Box
    sx={{
      borderRadius: 2,
      overflow: 'hidden',
      cursor: 'pointer',
      width: '100%',
      height: { xs: 300, md: 500 },
      minHeight: 0,
      minWidth: 0,
      maxHeight: { md: 500 },
      maxWidth: '100%',
      display: 'flex',
      alignItems: 'stretch',
      justifyContent: 'center',
    }}
    onClick={() => onOpenLightbox(media.link)}
  >
    <GalleryItem
      media={media}
      alt={media.link}
      src={media.link}
      sx={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        transition: (theme) => theme.transitions.create('opacity'),
        '&:hover': { opacity: 0.8 },
        borderRadius: 2,
      }}
    />
  </Box>
);

type ListGridThumbnailProps = {
  item: SparkMediaDto;
  isLastImage: boolean;
  remainingImages: number;
  onOpenLightbox: (src: string) => void;
};

const ListGridThumbnail = ({
  item,
  isLastImage,
  remainingImages,
  onOpenLightbox,
}: ListGridThumbnailProps) => (
  <Box
    sx={{
      position: 'relative',
      borderRadius: 2,
      cursor: 'pointer',
      overflow: 'hidden',
      width: '100%',
      height: '100%',
      minHeight: 0,
      minWidth: 0,
      display: 'flex',
      alignItems: 'stretch',
    }}
    onClick={() => onOpenLightbox(item.link)}
  >
    <GalleryItem
      media={item}
      alt={item.link}
      src={item.link}
      sx={{
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        transition: (theme) =>
          theme.transitions.create(['transform', 'opacity']),
        '&:hover': {
          transform: 'scale(1.05)',
          opacity: 0.9,
        },
        borderRadius: 2,
      }}
    />
    {isLastImage && remainingImages > 0 && (
      <RemainingImagesOverlay count={remainingImages} />
    )}
  </Box>
);

const ListGridPlaceholder = () => (
  <Box
    sx={{
      borderRadius: 2,
      backgroundColor: 'transparent',
      width: 1,
      height: 1,
      objectFit: 'cover',
      transition: (theme) => theme.transitions.create('transform'),
      '&:hover': {
        transform: 'scale(1.05)',
      },
    }}
  />
);

type ListGridViewProps = {
  media: SparkMediaDto[];
  onOpenLightbox: (src: string) => void;
};

const ListGridView = ({ media, onOpenLightbox }: ListGridViewProps) => {
  const thumbnails = media.slice(1, 5);
  const remainingCount = media.length - 5;
  const placeholderCount = Math.max(0, 4 - thumbnails.length);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 1,
        mb: { xs: 3, md: 5 },
        alignItems: 'stretch',
        width: '100%',
        maxHeight: { md: 500 },
      }}
    >
      <ListGridMainImage media={media[0]} onOpenLightbox={onOpenLightbox} />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridTemplateRows: '1fr 1fr',
          gap: 1,
          width: '100%',
          height: { xs: 300, md: 500 },
          minHeight: 0,
          minWidth: 0,
        }}
      >
        {thumbnails.map((item, index) => {
          const isLastImage = index === thumbnails.length - 1;
          return (
            <ListGridThumbnail
              key={item.link}
              item={item}
              isLastImage={isLastImage}
              remainingImages={remainingCount}
              onOpenLightbox={onOpenLightbox}
            />
          );
        })}
        {Array.from({ length: placeholderCount }).map((_, idx) => (
          <ListGridPlaceholder key={`gallery-placeholder-${idx}`} />
        ))}
      </Box>
    </Box>
  );
};

// ============================================================================
// Main Gallery Component
// ============================================================================

export const Gallery = ({ media, onOpenLightbox, variant = 'grid' }: Props) => {
  if (!media || media.length === 0) {
    return <NoImagesView />;
  }

  if (media.length === 1 || variant === 'single') {
    return <SingleImageView media={media[0]} onOpenLightbox={onOpenLightbox} />;
  }

  if (variant === 'grid') {
    return <GridView media={media} onOpenLightbox={onOpenLightbox} />;
  }

  if (variant === 'list-grid') {
    return <ListGridView media={media} onOpenLightbox={onOpenLightbox} />;
  }

  return null;
};
